import { useCallback, useMemo, useRef, useState } from 'react';
import {
  FRONT_YAW_TOLERANCE_DEG,
  LEFT_YAW_SIGN,
  PITCH_TOLERANCE_DEG,
  POSE_STABLE_TICKS_REQUIRED,
  PROFILE_YAW_MAX_DEG,
  PROFILE_YAW_MIN_DEG,
  RIGHT_YAW_SIGN,
  ROLL_TOLERANCE_DEG,
  VERIFICATION_SESSION_TIMEOUT_MS,
} from './poseVerificationConfig';
import type { FacePose, VerificationErrorKind, VerificationPhase, VibeAnalysisResult } from './types';

const POSE_ORDER: FacePose[] = ['front', 'left', 'right'];

/**
 * Per-gate breakdown behind a single poseValid() call — [VibePoseDebug]
 * diagnostic only, never used to gate anything itself. Lets the debug
 * overlay/logs show EXACTLY which of yaw/pitch/roll failed, instead of a
 * single collapsed boolean.
 */
export type PoseDebugSnapshot = {
  yaw: number | null;
  pitch: number | null;
  roll: number | null;
  yawFinite: boolean;
  pitchFinite: boolean;
  rollFinite: boolean;
  yawOk: boolean;
  pitchOk: boolean;
  rollOk: boolean;
  poseValid: boolean;
  stableTicks: number;
};

type State = {
  phase: VerificationPhase;
  faceDetected: boolean;
  faceCount: number;
  currentPose: FacePose;
  errorKind: VerificationErrorKind | null;
  sessionStartedAt: number;
  debug: PoseDebugSnapshot | null;
};

function initialState(): State {
  return {
    phase: 'requestingPermission',
    faceDetected: false,
    faceCount: 0,
    currentPose: 'front',
    errorKind: null,
    sessionStartedAt: Date.now(),
    debug: null,
  };
}

/**
 * Does this frame's face orientation satisfy the given pose? Returns the
 * full per-gate breakdown — [VibePoseDebug] section 1/2/3: this is where
 * "faceDetected=true but FRONT never qualifies" gets diagnosed, by exposing
 * exactly which of yaw/pitch/roll (if any) failed, and whether the native
 * value even arrived as a finite number in the first place (catches the
 * `Math.abs(undefined)`-style NaN class of bug explicitly, rather than
 * silently swallowing it into a single false).
 */
function evaluatePose(pose: FacePose, result: VibeAnalysisResult): Omit<PoseDebugSnapshot, 'stableTicks' | 'poseValid'> & { poseValid: boolean } {
  const rawYaw = result.headEulerAngleY;
  const rawPitch = result.headEulerAngleX;
  const rawRoll = result.headEulerAngleZ;

  const yawFinite = typeof rawYaw === 'number' && Number.isFinite(rawYaw);
  const pitchFinite = typeof rawPitch === 'number' && Number.isFinite(rawPitch);
  const rollFinite = typeof rawRoll === 'number' && Number.isFinite(rawRoll);

  const yaw = yawFinite ? (rawYaw as number) : null;
  const pitch = pitchFinite ? (rawPitch as number) : null;
  const roll = rollFinite ? (rawRoll as number) : null;

  // Missing/non-finite pitch or roll does not block a pose on its own —
  // ML Kit doesn't guarantee every axis on every frame, and this must fail
  // safe (permissive), not fail locked, for those two. Yaw is required —
  // there's no pose classification possible without it.
  const pitchOk = pitch == null || Math.abs(pitch) <= PITCH_TOLERANCE_DEG;
  const rollOk = roll == null || Math.abs(roll) <= ROLL_TOLERANCE_DEG;

  let yawOk = false;
  if (yaw != null) {
    if (pose === 'front') {
      yawOk = Math.abs(yaw) <= FRONT_YAW_TOLERANCE_DEG;
    } else {
      const sign = pose === 'left' ? LEFT_YAW_SIGN : RIGHT_YAW_SIGN;
      const signedYaw = yaw * sign;
      yawOk = signedYaw >= PROFILE_YAW_MIN_DEG && signedYaw <= PROFILE_YAW_MAX_DEG;
    }
  }

  return {
    yaw, pitch, roll,
    yawFinite, pitchFinite, rollFinite,
    yawOk, pitchOk, rollOk,
    poseValid: yawOk && pitchOk && rollOk,
  };
}

/**
 * Drives the live Vibe Verification flow: front face -> left profile ->
 * right profile, each auto-captured once its pose is confidently detected
 * and held stable for POSE_STABLE_TICKS_REQUIRED consecutive analysis
 * ticks. No hand gesture, no liveness challenge — face-pose only. Holds
 * only booleans/numbers/timestamps — never frames or images — so the ML
 * analysis stays ephemeral.
 */
export function useVibeVerificationStateMachine() {
  const [state, setState] = useState<State>(initialState);
  // Consecutive analysis ticks where the CURRENT pose was valid. Reset to 0
  // on any invalid tick or on advancing to the next pose. A ref, not state
  // — incremented synchronously inside the setState updater below, so it
  // can never see a stale value across ticks the way state read from a
  // closure could.
  const consecutiveValidTicksRef = useRef(0);

  const startLiveCamera = useCallback(() => {
    consecutiveValidTicksRef.current = 0;
    setState(prev => ({ ...prev, phase: 'front', sessionStartedAt: Date.now() }));
  }, []);

  const setPermissionDenied = useCallback(() => {
    setState(prev => ({ ...prev, phase: 'permissionDenied', errorKind: 'permission_denied' }));
  }, []);

  const setError = useCallback((kind: VerificationErrorKind) => {
    if (__DEV__) console.log('[VibePoseDebug] setError', { kind });
    setState(prev => ({ ...prev, phase: 'error', errorKind: kind }));
  }, []);

  const reset = useCallback(() => {
    consecutiveValidTicksRef.current = 0;
    setState(initialState());
  }, []);

  const requestFallback = useCallback(() => {
    setState(prev => ({ ...prev, phase: 'fallback' }));
  }, []);

  const markCapturing = useCallback(() => {
    if (__DEV__) console.log('[VibePoseDebug] AUTO CAPTURE START');
    setState(prev => ({ ...prev, phase: 'capturing' }));
  }, []);

  /** Called after a single pose's evidence photo is safely captured — advances to the next pose or finishes. */
  const advancePose = useCallback(() => {
    consecutiveValidTicksRef.current = 0;
    setState(prev => {
      const currentIndex = POSE_ORDER.indexOf(prev.currentPose);
      const nextPose = POSE_ORDER[currentIndex + 1];
      if (__DEV__) console.log('[VibePoseDebug] STEP', prev.currentPose.toUpperCase(), '->', (nextPose ?? 'DONE').toUpperCase());
      if (!nextPose) {
        return { ...prev, phase: 'captured' };
      }
      return { ...prev, phase: nextPose, currentPose: nextPose };
    });
  }, []);

  const processAnalysis = useCallback((result: VibeAnalysisResult) => {
    setState(prev => {
      if (prev.phase !== 'front' && prev.phase !== 'left' && prev.phase !== 'right') {
        return prev;
      }

      const now = Date.now();
      if (now - prev.sessionStartedAt > VERIFICATION_SESSION_TIMEOUT_MS) {
        if (__DEV__) console.log('[VibePoseDebug] session timeout, falling back to manual review');
        return { ...prev, phase: 'fallback' };
      }

      if (result.faceCount > 1) {
        consecutiveValidTicksRef.current = 0;
        if (__DEV__) console.log('[VibePoseDebug] multiple faces', { faceCount: result.faceCount });
        return { ...prev, faceCount: result.faceCount, faceDetected: false, errorKind: 'multiple_faces' };
      }

      const faceDetected = result.faceDetected;
      const evaluation = evaluatePose(prev.currentPose, result);
      const isValid = faceDetected && evaluation.poseValid;

      if (!isValid) consecutiveValidTicksRef.current = 0;
      else consecutiveValidTicksRef.current += 1;

      const debug: PoseDebugSnapshot = { ...evaluation, stableTicks: consecutiveValidTicksRef.current };

      if (__DEV__) {
        console.log('[VibePoseDebug][JS] received', {
          pose: prev.currentPose,
          faceDetected,
          faceCount: result.faceCount,
          yawRaw: result.headEulerAngleY,
          pitchRaw: result.headEulerAngleX,
          rollRaw: result.headEulerAngleZ,
        });
        console.log('[VibePoseDebug] gates', {
          pose: prev.currentPose,
          yaw: debug.yaw, pitch: debug.pitch, roll: debug.roll,
          yawFinite: debug.yawFinite, pitchFinite: debug.pitchFinite, rollFinite: debug.rollFinite,
          yawOk: debug.yawOk, pitchOk: debug.pitchOk, rollOk: debug.rollOk,
          poseValid: debug.poseValid,
          stableTicks: debug.stableTicks,
          required: POSE_STABLE_TICKS_REQUIRED,
        });
      }

      if (!isValid) {
        return { ...prev, faceCount: result.faceCount, faceDetected, errorKind: null, debug };
      }

      if (consecutiveValidTicksRef.current < POSE_STABLE_TICKS_REQUIRED) {
        return { ...prev, faceCount: result.faceCount, faceDetected, errorKind: null, debug };
      }

      // Stable for long enough — hand off to the caller to actually
      // capture the evidence photo (see VibeVerificationCameraModal, which
      // calls markCapturing() then advancePose() once the file is saved).
      if (__DEV__) console.log('[VibePoseDebug]', prev.currentPose.toUpperCase(), 'QUALIFIED');
      return { ...prev, faceCount: result.faceCount, faceDetected, phase: 'poseConfirmed', errorKind: null, debug };
    });
  }, []);

  return useMemo(
    () => ({
      state,
      startLiveCamera,
      setPermissionDenied,
      setError,
      reset,
      requestFallback,
      markCapturing,
      advancePose,
      processAnalysis,
    }),
    [state, startLiveCamera, setPermissionDenied, setError, reset, requestFallback, markCapturing, advancePose, processAnalysis],
  );
}
